import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from './config/axios';
import { WA_PRESET_COLORS } from './features/employees/styles/personalakteStyles';
import EmployeeService from './features/employees/services/EmployeeService';
import { Employee } from './hooks/useEmployeeData';
import { toast } from 'react-toastify';

/**
 * Interface for location object structure
 */
interface Location {
  id: number;
  name: string;
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for work area object structure (API response)
 */
interface WorkArea {
  id: number;
  name: string;
  address: string;
  location?: number;
  radius_meters: number;
  color: string;
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for work area edit form data
 */
interface WorkAreaEditFormData {
  location: string;
  name: string;
  address: string;
  radius_meters: number;
  color: string;
}

/**
 * Interface for employee checkbox state
 */
interface CheckedMap {
  [employeeId: number]: boolean;
}

/**
 * Interface for work area update payload
 */
interface WorkAreaUpdatePayload {
  location: number | null;
  name: string;
  address: string;
  radius_meters: number;
  color: string;
}

function WorkAreaEdit(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();                     // Work‑Area‑ID (string)
  const waId = parseInt(id || '0', 10);                          // int für Vergleiche
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/organization?tab=work-areas';

  /* ---------- 1. State ---------- */
  const [locations, setLocations] = useState<Location[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);          // komplette Liste
  const [checkedMap, setCheckedMap] = useState<CheckedMap>({});        // empId ➜ bool (UI)
  const [saving, setSaving] = useState<boolean>(false);

  const [formData, setFormData] = useState<WorkAreaEditFormData>({
    location: '',
    name: '',
    address: '',
    radius_meters: 50,
    color: '#5271A5',
  });

  /* ---------- 2. Daten laden ---------- */
  useEffect(() => {
    if (!id) return;

    // (a) Work‑Area
    axiosInstance.get<WorkArea>(`/api/admin/work-areas/${id}/`)
      .then(res => {
        setFormData({
          location: res.data.location ? String(res.data.location) : '',
          name: res.data.name || '',
          address: res.data.address || '',
          radius_meters: res.data.radius_meters ?? 50,
          color: res.data.color || '#5271A5',
        });
      })
      .catch((err: any) => console.error('Error loading WorkArea:', err));

    // (b) Locations (Dropdown)
    axiosInstance.get<Location[] | { results: Location[] }>('/api/admin/locations/')
      .then(res => {
        // Handle both array and paginated response formats
        const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setLocations(data);
      })
      .catch((err: any) => console.error('Error loading Locations:', err));

    // (c) Employees (Checkbox‑Liste)
    EmployeeService.fetchAllEmployees()
      .then(employees => {
        setEmployees(employees);
        // Vorbelegung der Checkboxen
        const initial: CheckedMap = {};
        employees.forEach(emp => {
          initial[emp.id] = Array.isArray(emp.work_areas)
            ? emp.work_areas.includes(waId)
            : false;
        });
        setCheckedMap(initial);
      })
      .catch((err: any) => console.error('Error loading Employees:', err));
  }, [id, waId]);

  /* ---------- 3. Form‑Handling ---------- */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' ? Number(value) : value 
    }));
  };

  const toggleEmployee = (empId: number): void => {
    setCheckedMap(prev => ({ ...prev, [empId]: !prev[empId] }));
  };

  /* ---------- 4. Speichern ---------- */
  const handleUpdate = async (): Promise<void> => {
    if (!id) return;
    
    setSaving(true);
    try {
      /* 4.1 Work‑Area selbst patchen ------------- */
      const payload: WorkAreaUpdatePayload = {
        location: formData.location ? parseInt(formData.location, 10) : null,
        name: formData.name,
        address: formData.address,
        radius_meters: formData.radius_meters,
        color: formData.color,
      };
      await axiosInstance.patch<WorkArea>(`/api/admin/work-areas/${id}/`, payload);

      /* 4.2 Employees aktualisieren -------------- */
      const patchPromises = employees.map(emp => {
        const shouldHave = !!checkedMap[emp.id];
        const hasNow = Array.isArray(emp.work_areas) && emp.work_areas.includes(waId);

        if (shouldHave !== hasNow) {
          const newArr = shouldHave
            ? [...(emp.work_areas || []), waId]
            : (emp.work_areas || []).filter(x => x !== waId);

          return EmployeeService.updateEmployee(emp.id, { work_areas: newArr });
        }
        return null;
      }).filter(Boolean);

      await Promise.all(patchPromises);

      /* 4.3 Fertig */
      navigate(returnTo);
    } catch (err: any) {
      console.error('Error updating WorkArea or Employees:', err);
      toast.error(`Speichern fehlgeschlagen: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  /* ---------- 5. Helper Functions ---------- */
  const selectAll = (): void => {
    const newMap: CheckedMap = {};
    sortedEmployees.forEach(emp => { newMap[emp.id] = true; });
    setCheckedMap(newMap);
  };

  const deselectAll = (): void => {
    setCheckedMap({});
  };

  const handleCancel = (): void => {
    navigate(returnTo);
  };

  /* ---------- 6. Derived State ---------- */
  // Mitarbeitende alphabetisch sortieren
  const sortedEmployees = [...employees].sort((a, b) => {
    const nameA = ((a.first_name || '') + ' ' + (a.last_name || '')).toLowerCase();
    const nameB = ((b.first_name || '') + ' ' + (b.last_name || '')).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return (
    <div data-testid="wae-root" style={{
      maxWidth: 620,
      margin: '40px auto',
      background: '#fff',
      borderRadius: 14,
      boxShadow: '0 8px 32px rgba(30,40,90,0.13)',
      padding: '36px 38px 28px 38px',
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: '1.07rem',
      minHeight: 480
    }}>
      <h2 data-testid="wae-title" style={{margin:0, marginBottom: 24, fontWeight:700, color:'#002b80', fontSize:'1.35rem', letterSpacing:'-0.5px'}}>Edit Work Area</h2>

      {/* Name zuerst, dann Location */}
      <div style={{display:'flex', gap:18, marginBottom:18}}>
        <div style={{flex:1}}>
          <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Name:</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            style={{ width:'100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
          />
        </div>
        <div style={{flex:1}} data-testid="wae-location-group">
          <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Location:</label>
          <select data-testid="wae-location-select"
            name="location"
            value={formData.location}
            onChange={handleChange}
            style={{ width:'100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
          >
            <option value="">-- None / no Location --</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{display:'flex', gap:18, marginBottom:18}} data-testid="wae-address-group">
        <div style={{flex:2}}>
          <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Address:</label>
          <input data-testid="wae-address-input"
            name="address"
            value={formData.address}
            onChange={handleChange}
            style={{ width:'100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
          />
        </div>
        <div style={{flex:1, display:'flex', gap:10}} data-testid="wae-radius-group">
          <div style={{flex:1}}>
            <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Radius:</label>
            <div style={{display:'flex', alignItems:'center', gap:6}}>
              <input data-testid="wae-radius-input"
                type="number"
                name="radius_meters"
                value={formData.radius_meters}
                onChange={handleChange}
                style={{ width:'100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 7, fontSize:'1rem' }}
              />
              <span style={{fontSize:'1rem', color:'#555'}}>m</span>
            </div>
          </div>
          <div style={{flex:1}}>
            <label style={{fontWeight:500, marginBottom:4, display:'block'}}>Color:</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {WA_PRESET_COLORS.map((c) => {
                const selected = formData.color.toLowerCase() === c.toLowerCase();
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, color: c }))}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: c,
                      border: selected ? '2px solid #fff' : '2px solid transparent',
                      boxShadow: selected ? `0 0 0 2px ${c}, 0 1px 4px rgba(0,0,0,0.18)` : 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={c}
                  >
                    {selected && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M3 7l3 3 5-5.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <hr style={{margin:'28px 0 18px 0', border:'none', borderTop:'1.5px solid #f0f0f0'}}/>
      <h3 style={{margin:'0 0 8px 0', fontWeight:600, color:'#002b80', fontSize:'1.08rem'}}>Assign Employees to Work Area:</h3>
      <div style={{display:'flex', gap:10, marginBottom:8}}>
        <button type="button" onClick={selectAll} style={{background:'#f5f5f5', border:'1px solid #e0e0e0', borderRadius:6, padding:'4px 14px', cursor:'pointer', fontWeight:500, fontSize:'0.98rem'}}>Select All</button>
        <button type="button" onClick={deselectAll} style={{background:'#f5f5f5', border:'1px solid #e0e0e0', borderRadius:6, padding:'4px 14px', cursor:'pointer', fontWeight:500, fontSize:'0.98rem'}}>Deselect All</button>
      </div>
      <div style={{ maxHeight:'220px', overflowY:'auto', border:'1px solid #e0e0e0', borderRadius:8, padding:'10px 12px', background:'#fafbfc', marginBottom:18 }}>
        {sortedEmployees.map(emp => (
          <div key={emp.id} style={{marginBottom: 7, display:'flex', alignItems:'center', gap:8}}>
            <input
              type="checkbox"
              checked={!!checkedMap[emp.id]}
              onChange={() => toggleEmployee(emp.id)}
              id={`emp-${emp.id}`}
              style={{ width:18, height:18, accentColor:'#002b80', cursor:'pointer', marginRight:2 }}
            />
            <label htmlFor={`emp-${emp.id}`} style={{ marginLeft:'6px', fontSize:'1rem', color:'#222', fontFamily:'Inter, Arial, sans-serif'}}>
              {emp.first_name || ''} {emp.last_name || ''}
            </label>
          </div>
        ))}
        {!sortedEmployees.length && <p style={{color:'#888'}}>No employees loaded …</p>}
      </div>

      <div style={{display:'flex', justifyContent:'flex-end', gap:12, marginTop: 18}}>
        <button data-testid="wae-cancel-button"
          onClick={handleCancel}
          style={{
            background:'#f5f5f5', color:'#03045e', border:'1px solid #e0e0e0', borderRadius:6, padding:'8px 20px', cursor:'pointer', fontWeight:500, fontSize:'1rem', transition:'background 0.18s'}}
        >
          Cancel
        </button>
        <button data-testid="wae-update-button"
          onClick={handleUpdate}
          disabled={saving}
          style={{
            background:'#002b80', color:'#fff', border:'none', borderRadius:6, padding:'8px 22px', cursor:'pointer', fontWeight:600, fontSize:'1rem', boxShadow:'0 1px 2px rgba(30,40,90,0.07)', transition:'background 0.18s'}}
        >
          {saving ? 'Saving…' : 'Update'}
        </button>
      </div>
    </div>
  );
}

export default WorkAreaEdit; 
